import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import RelatedServicesNav from '../components/RelatedServicesNav';
import StickyMobileCta from '../components/StickyMobileCta';
import {
  ScanLine, ShieldCheck, Zap, Bone, Activity, HeartPulse, Dog, Cat, Bandage,
  ClipboardCheck, MessageSquare, Clock, Microscope, CheckCircle2, MapPin,
  Phone, MessageCircle, CalendarDays, ArrowRight, AlertCircle, ClipboardList,
  BookOpen, HandHeart, Sparkles, Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CONTACT, GOOGLE_RATING } from '../data/mock';
import { cdnImg } from '../utils/cdn';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

const ASSET = 'https://customer-assets.emergentagent.com/job_petcare-preview-1/artifacts';
const DR_PORTRAIT = `${ASSET}/jnhxjj9q_WhatsApp%20Image%202026-07-08%20at%2012.31.40.jpeg`;
const HERO_IMAGE = 'https://images.unsplash.com/photo-1638202993928-7267aad84c31?auto=format&fit=crop&w=1400&q=80';

const TITLE = 'Dog & Cat X-Ray in Noida | Digital Veterinary X-Ray';
const DESCRIPTION = 'Digital X-ray for dogs and cats in Noida at Ichi & Ori Pet Care. Veterinary radiography for fractures, chest, abdomen and diagnostic evaluation. Book a vet appointment.';
const CANONICAL = 'https://ichiandoripetcare.com/pet-x-ray-noida';
const set = (sel, attr, val) => { const el = document.head.querySelector(sel); if (el) el.setAttribute(attr, val); };

const WHY_XRAY_MATTERS = [
  { Icon: Eye,           title: 'See what an exam cannot',    text: 'X-rays quietly reveal fractures, foreign bodies, chest changes and joint issues that a physical examination alone can never show.' },
  { Icon: Zap,           title: 'Fast, in-clinic answers',    text: 'Digital DR imaging is captured and reviewed on-screen within minutes, so decisions do not have to wait for outside labs.' },
  { Icon: ShieldCheck,   title: 'Low-dose, safe imaging',     text: 'Modern digital radiography uses significantly lower radiation doses than older film systems, making it safer for your pet and the team.' },
  { Icon: HandHeart,     title: 'Gentle, calm positioning',   text: 'Nervous pets are given the time and quiet handling they need. Sedation is discussed and used only where truly required.' },
  { Icon: ClipboardCheck,title: 'Guides real decisions',      text: 'A well-taken X-ray often changes the treatment plan on the same visit, avoiding both under-treatment and unnecessary escalation.' },
  { Icon: Microscope,    title: 'Complements diagnostics',    text: 'Imaging works best alongside bloodwork, ECG and physical examination. Together, they build a full picture of what is going on.' },
];

const WHEN_XRAY = [
  { Icon: Bone,         title: 'Limping or reluctance to move',  text: 'A dog fracture X-ray or cat fracture X-ray helps confirm the cause of a limp, whether it is a fracture, joint disease or a soft-tissue injury, and guides safe next steps.' },
  { Icon: Activity,     title: 'Coughing or breathing issues',   text: 'A chest X-ray for pets is central to evaluating pneumonia, heart disease, tumours and airway conditions in dogs and cats.' },
  { Icon: HeartPulse,   title: 'Suspected foreign body',         text: 'If your dog or cat has swallowed something they shouldn\u2019t have, an abdominal X-ray for dogs and cats often confirms whether it is safe to wait or needs urgent removal.' },
  { Icon: AlertCircle,  title: 'Abdominal pain or bloating',     text: 'Abdominal radiographs help evaluate organ size, gas patterns and possible obstructions in both dogs and cats.' },
  { Icon: Sparkles,     title: 'Recurring urinary problems',     text: 'Bladder stones and urinary tract abnormalities in dogs and cats frequently show up on plain radiographs and shape the treatment plan.' },
  { Icon: Bandage,      title: 'Post-injury evaluation',         text: 'After any fall, road accident or bite injury, an X-ray for dogs or cats helps rule out fractures, chest bleeds and internal damage before they worsen.' },
];

const PROCESS = [
  { step: '01', title: 'Consultation & clinical exam',  text: 'The veterinarian examines your pet and decides whether imaging is truly the right next step, and which views will provide the most useful information.' },
  { step: '02', title: 'Positioning & imaging',         text: 'Your pet is gently positioned by trained staff. Most cases do not need sedation. Where it is needed, it is discussed with you first.' },
  { step: '03', title: 'On-screen review',              text: 'Digital DR images are available within minutes and reviewed on a clinical monitor with proper contrast, zoom and windowing controls.' },
  { step: '04', title: 'Findings explained clearly',    text: 'You see the images alongside the veterinarian and hear the findings in plain language, with clinical significance and next steps.' },
  { step: '05', title: 'Referral where useful',         text: 'Complex or ambiguous cases are shared with radiology specialists for a second opinion when it will meaningfully change the plan.' },
  { step: '06', title: 'Records shared with you',       text: 'Images and reports are stored digitally and can be shared with you and any referral clinic your pet may visit later.' },
];

const SAFETY = [
  { Icon: ShieldCheck,  title: 'Low-dose modern equipment',  text: 'Digital DR radiography significantly lowers the radiation dose needed to produce diagnostic-quality images, compared with older film-based systems.' },
  { Icon: ClipboardList,title: 'Only when clinically indicated', text: 'X-rays are recommended only when they will meaningfully change the plan, never as a default add-on to a consultation.' },
  { Icon: Clock,        title: 'Short, controlled exposure',  text: 'Each image involves a very brief exposure. Positioning and technique are optimised to reduce repeat images.' },
  { Icon: HandHeart,    title: 'Sedation used sparingly',     text: 'Whenever your pet allows calm positioning, we prefer not to sedate. Sedation is used only where it makes the visit truly safer.' },
];

const WHY_ICHI = [
  'In-house Digital DR radiography',
  'Findings reviewed on the same visit',
  'Gentle, calm positioning',
  'Sedation only where genuinely needed',
  'Clear image explanations for pet parents',
  'Digital storage and easy sharing',
  'Coordination with referral radiologists',
  'Honest guidance on next steps',
];

const LOCATIONS = ['Sector 90','Sector 137','Sector 143','Sector 168','Sector 93','Sector 100','Noida Expressway','Nearby residential societies'];

const FAQS = [
  { q: 'What is a Digital DR X-ray?',               a: 'Digital DR (Direct Radiography) is a modern form of pet imaging where images are captured directly onto a digital sensor. The output is available in seconds, at higher clarity and typically at lower radiation doses than older film-based X-rays.' },
  { q: 'Is a pet X-ray safe?',                      a: 'Yes, when performed with modern digital equipment and only when clinically indicated. Each exposure is brief and controlled. The diagnostic value of a well-taken X-ray almost always outweighs the very small radiation dose involved.' },
  { q: 'Will my pet need sedation for the X-ray?',  a: 'Most pets can be gently positioned without sedation. In anxious, painful or highly restless cases, a light sedative may be recommended to keep the visit safer and reduce repeat exposures. This is always discussed before use.' },
  { q: 'How long does the imaging take?',           a: 'A simple X-ray usually takes 15 to 30 minutes from arrival to review. More views or more anxious pets may take longer. You typically leave with a clear next step in the same visit.' },
  { q: 'What kinds of X-rays do you offer?',        a: 'We routinely perform chest, abdominal, orthopaedic, spinal and dental-area radiographs for dogs and cats using Digital DR equipment.' },
  { q: 'Can X-ray images be shared with another clinic?', a: 'Yes. Digital images and reports can be shared with any referral clinic or specialist your pet may need to visit. This avoids duplicate imaging and saves time.' },
  { q: 'Is fasting required before a pet X-ray?',   a: 'Simple X-rays usually do not require fasting. Abdominal imaging and any case likely to need sedation may need short-term fasting. Instructions are shared in advance.' },
  { q: 'Can X-rays replace bloodwork or ultrasound?', a: 'Not entirely. Each diagnostic tool answers different questions. X-rays are especially strong for bones, gas patterns and larger soft-tissue abnormalities. Bloodwork and ultrasound are recommended when they add real information.' },
  { q: 'How much does a pet X-ray cost?',           a: 'Costs depend on the number of views and whether sedation is required. A clear estimate is always shared before imaging, and there are no hidden fees added at billing.' },
  { q: 'How do I book a pet X-ray appointment?',    a: 'Use the Request Appointment button on this page, message us on WhatsApp at +91 9560925999, or call the clinic between 10:30 AM and 8:30 PM. A short description of your pet\'s symptoms helps us plan the right imaging.' },
];

const PetXrayNoida = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const orig = {
      d: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      c: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
    };
    set('meta[name="description"]', 'content', DESCRIPTION);
    set('link[rel="canonical"]', 'href', CANONICAL);
    set('meta[property="og:title"]', 'content', TITLE);
    set('meta[property="og:description"]', 'content', DESCRIPTION);
    set('meta[property="og:url"]', 'content', CANONICAL);
    return () => {
      if (orig.d != null) set('meta[name="description"]', 'content', orig.d);
      if (orig.c != null) set('link[rel="canonical"]', 'href', orig.c);
    };
  }, []);

  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent("Hi, I'd like to book a pet X-ray at Ichi & Ori Pet Care.")}`;
  const faqSchema = { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) };
  const vetSchema = {
    '@context': 'https://schema.org', '@type': 'VeterinaryCare',
    name: 'Ichi and Ori Pet Care, Digital Veterinary X-Ray for Dogs and Cats in Noida',
    url: CANONICAL, telephone: CONTACT.phoneRaw, priceRange: '\u20B9\u20B9',
    address: { '@type': 'PostalAddress', streetAddress: 'G11, Tower C, Noida World One', addressLocality: 'Sector 90, Noida', addressRegion: 'Uttar Pradesh', postalCode: '201305', addressCountry: 'IN' },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: String(GOOGLE_RATING?.rating || 4.9), reviewCount: String(GOOGLE_RATING?.count || 107), bestRating: '5' },
    areaServed: { '@type': 'City', name: 'Noida' },
  };
  const serviceSchema = {
    '@context': 'https://schema.org', '@type': 'Service',
    name: 'Digital Veterinary X-Ray for Dogs and Cats in Noida',
    serviceType: 'Veterinary Radiography',
    description: 'Digital veterinary X-ray (radiography) for dogs and cats in Noida. Helps evaluate fractures, bone injuries, chest conditions, abdominal concerns and other diagnostic needs.',
    provider: { '@type': 'VeterinaryCare', name: 'Ichi and Ori Pet Care', url: 'https://ichiandoripetcare.com' },
    areaServed: { '@type': 'City', name: 'Noida' },
    audience: { '@type': 'PeopleAudience', audienceType: 'Pet Parents (Dogs and Cats)' },
    url: CANONICAL,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ichiandoripetcare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Pet X-Ray in Noida', item: CANONICAL },
    ],
  };

  return (
    <div className="bg-white" data-testid="pet-xray-noida-page">
      <Helmet>
        <title>{TITLE}</title>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(vetSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* 1 — HERO */}
      <section className="relative bg-gradient-to-br from-[#1a0b3d] via-[#3b1d76] to-[#5b3eb5] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_25%_20%,#e870b6_0%,transparent_40%),radial-gradient(circle_at_80%_75%,#5b3eb5_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 sm:py-1.5 rounded-full bg-[#e870b6]/20 border border-[#e870b6]/40 text-[#ffc1e0] text-[11px] sm:text-xs font-bold tracking-[0.18em] mb-6">
              <ScanLine className="w-3.5 h-3.5 flex-shrink-0" />DOG &amp; CAT X-RAY · NOIDA
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] mb-6">Digital X-Ray for Dogs &amp; Cats in Noida</h1>
            <p className="text-base sm:text-lg text-white/85 leading-relaxed mb-9 max-w-xl">Ichi &amp; Ori Pet Care provides digital veterinary X-ray for dogs and cats in Noida. Pet radiography is used to help evaluate fractures, bone injuries, chest conditions, abdominal concerns and other diagnostic needs, based on a veterinary assessment during your visit.</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-4">
              <Link to="/book" onClick={() => trackBookButtonClick('pet_xray_hero')} className="w-full sm:w-auto">
                <Button data-testid="pet-xray-hero-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button>
              </Link>
              <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('pet_xray_hero')} className="w-full sm:w-auto">
                <Button data-testid="pet-xray-hero-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />Chat on WhatsApp</Button>
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('pet_xray_hero')} className="w-full sm:w-auto sm:ml-1">
                <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                  <Phone className="w-4 h-4" /> Call Now
                </span>
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/15 aspect-[4/5]">
              <img src={HERO_IMAGE} alt="Digital DR X-ray imaging station for dogs and cats at Ichi and Ori Pet Care, Noida — placeholder image" loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <RelatedServicesNav current="/pet-x-ray-noida" />

      {/* 2 — WHY X-RAY MATTERS */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHY IT MATTERS</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Digital Veterinary X-Ray for Dogs and Cats</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">A veterinary X-ray is one of the fastest ways to answer questions that a physical examination alone cannot, whether it is a limping dog, a coughing cat or an abdominal concern. Used well, digital radiography shapes the treatment plan on the same visit.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_XRAY_MATTERS.map((r, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-2xl p-7 border border-[#f3d6f5]/60 hover:shadow-md transition-all flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-white border border-[#f5d3e6] flex items-center justify-center mb-4 flex-shrink-0"><r.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                <h3 className="font-bold text-[#1a0b3d] text-lg mb-2 leading-snug">{r.title}</h3>
                <p className="text-sm text-[#3b1d76]/85 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — WHEN AN X-RAY IS RECOMMENDED */}
      <section className="bg-[#1a0b3d] text-white py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_15%_20%,#5b3eb5_0%,transparent_45%),radial-gradient(circle_at_85%_80%,#e870b6_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#ffc1e0] mb-4">WHEN X-RAYS HELP</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.08]">When Does a Dog or Cat Need an X-Ray?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHEN_XRAY.map((s, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 flex flex-col h-full">
                <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center mb-4 flex-shrink-0"><s.Icon className="w-5 h-5 text-[#ffc1e0]" /></div>
                <h3 className="font-bold text-white text-base mb-2 leading-snug">{s.title}</h3>
                <p className="text-sm text-white/75 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — HOW IT WORKS */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">HOW IT WORKS</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">What Can a Pet X-Ray Help Evaluate?</h2>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PROCESS.map((s, i) => (
              <li key={i} className="bg-[#fdf7fb] rounded-2xl p-6 border border-[#f3d6f5]/60 flex gap-5 items-start">
                <span className="text-4xl font-extrabold text-[#e870b6]/50 leading-none tracking-tight">{s.step}</span>
                <div>
                  <h3 className="font-bold text-[#1a0b3d] text-lg mb-1.5 leading-snug">{s.title}</h3>
                  <p className="text-sm text-[#3b1d76]/85 leading-relaxed">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 5 — SAFETY */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">SAFETY FIRST</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Careful, calm and only when needed.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SAFETY.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-[#f3d6f5]/60 flex flex-col h-full">
                <div className="w-11 h-11 rounded-xl bg-[#fdf0f8] border border-[#f5d3e6] flex items-center justify-center mb-4 flex-shrink-0"><s.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                <h3 className="font-bold text-[#1a0b3d] text-base mb-2 leading-snug">{s.title}</h3>
                <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 — WHY CHOOSE ICHI & ORI */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-[minmax(240px,360px)_1fr] gap-10 lg:gap-14 items-start">
          <div>
            <div className="rounded-[24px] overflow-hidden shadow-xl border border-[#f5d3e6] bg-white">
              <img src={cdnImg(DR_PORTRAIT, { w: 720, h: 900, fit: 'cover', a: 'center' })} alt="Dr Kaushal Singh Yadav, veterinarian at Ichi and Ori Pet Care, Sector 90 Noida" loading="lazy" decoding="async" className="w-full h-auto object-cover" />
            </div>
          </div>
          <div>
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHY CHOOSE ICHI &amp; ORI</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08] mb-6">Imaging that leads to a real next step.</h2>
            <div className="space-y-4 text-[#3b1d76]/85 text-[15px] sm:text-[16px] leading-[1.85] mb-8">
              <p>Every X-ray is ordered for a reason and reviewed with you on the same visit. You see the image, hear what it means and understand exactly why the plan changed, or didn&rsquo;t.</p>
              <p>Where a case needs a second opinion, we work with trusted radiology partners without you having to travel or repeat imaging.</p>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {WHY_ICHI.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#fdf0f8] border border-[#f5d3e6] flex items-center justify-center flex-shrink-0 mt-0.5"><CheckCircle2 className="w-4 h-4 text-[#e870b6]" /></span>
                  <span className="text-[#1a0b3d] text-[15px] font-semibold">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/book" onClick={() => trackBookButtonClick('pet_xray_why')} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1a0b3d] hover:bg-[#3b1d76] text-white text-sm font-semibold transition-colors">Book an X-Ray Appointment <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* 7 — AREAS SERVED */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">AREAS WE SERVE</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Veterinary X-Ray Services in Noida</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {LOCATIONS.map((l, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#f3d6f5] text-[#1a0b3d] text-sm font-semibold">
                <MapPin className="w-3.5 h-3.5 text-[#e870b6]" /> {l}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 8 — FAQ */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[880px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">FREQUENTLY ASKED</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Pet X-ray questions, answered clearly.</h2>
          </div>
          <Accordion type="single" collapsible defaultValue="faq-0" className="divide-y divide-[#f5d3e6] border-y border-[#f5d3e6] bg-[#fdf7fb] rounded-2xl" data-testid="pet-xray-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="px-4">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-[#1a0b3d] font-semibold text-base sm:text-lg hover:no-underline py-5 sm:py-6">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#3b1d76]/85 text-sm sm:text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 9 — FINAL CTA */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="rounded-[32px] bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] text-white py-16 sm:py-20 px-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_20%_30%,#e870b6_0%,transparent_45%)]" />
            <div className="relative text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-[1.08]">A clearer picture, on the same visit.</h2>
              <p className="text-white/85 mb-10 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">If your pet has a limp, a cough or something that just doesn&rsquo;t feel right, book a consultation. Imaging is available in-house when it will help us decide the next step.</p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                <Link to="/book" onClick={() => trackBookButtonClick('pet_xray_final')} className="w-full sm:w-auto">
                  <Button data-testid="pet-xray-final-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] rounded-full px-8 py-6 font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button>
                </Link>
                <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('pet_xray_final')} className="w-full sm:w-auto">
                  <Button data-testid="pet-xray-final-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] rounded-full px-8 py-6 font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />WhatsApp</Button>
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('pet_xray_final')} className="w-full sm:w-auto sm:ml-1">
                  <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                    <Phone className="w-4 h-4" /> Call Now
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <StickyMobileCta source="petxray" />
    </div>
  );
};

export default PetXrayNoida;
